import { useState } from 'react';
import { Link } from 'react-router-dom';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import Breadcrumb from '@/components/feature/Breadcrumb';
import SEOMeta from '@/components/base/SEOMeta';

export default function AboutPage() {
  const [formStatus, setFormStatus] = useState<'idle' | 'submitting' | 'success' | 'error'>('idle');
  const [formError, setFormError] = useState('');

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    const form = e.currentTarget;
    const honeypotField = form.querySelector<HTMLInputElement>('input[name="phone_alt"]');
    if (honeypotField && honeypotField.value.trim() !== '') {
      setFormStatus('success');
      return;
    }

    setFormStatus('submitting');
    setFormError('');

    try {
      const formData = new FormData(form);
      const response = await fetch('https://readdy.ai/api/form/d9qslmvnahb7382ovf1g', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/x-www-form-urlencoded',
        },
        body: new URLSearchParams(formData as unknown as Record<string, string>).toString(),
      });

      const responseText = await response.text();
      let parsed;
      try {
        parsed = JSON.parse(responseText);
      } catch {
        parsed = null;
      }

      if (response.ok && parsed?.code === 'OK') {
        setFormStatus('success');
        form.reset();
      } else {
        const serverMsg = parsed?.meta?.message || parsed?.message || parsed?.meta?.detail || responseText || '提交失败，请稍后重试';
        if (serverMsg.includes('spam') || serverMsg.includes('form data is spam')) {
          setFormError('提交失败，请稍后重试');
        } else {
          setFormError(serverMsg);
        }
        setFormStatus('error');
      }
    } catch {
      setFormError('网络错误，请检查网络连接后重试');
      setFormStatus('error');
    }
  };

  const orgSchema = {
    '@context': 'https://schema.org',
    '@type': 'Organization',
    name: '黑料在线观看',
    url: 'https://heiliao-online.com',
    description: '最新最全的视频内容平台，提供免费在线观看服务，汇聚热门资讯与精彩视频。',
    foundingDate: '2020',
    address: {
      '@type': 'PostalAddress',
      streetAddress: '媒体大道88号',
      addressLocality: '北京市',
      addressRegion: '朝阳区',
      postalCode: '100020',
      addressCountry: 'CN',
    },
    contactPoint: {
      '@type': 'ContactPoint',
      telephone: '400-888-0000',
      contactType: 'customer service',
      email: 'contact@heiliao-online.com',
    },
    sameAs: [
      'https://weibo.com/heiliao-online',
      'https://weixin.qq.com/heiliao-online',
    ],
  };

  const faqSchema = {
    '@context': 'https://schema.org',
    '@type': 'FAQPage',
    mainEntity: [
      {
        '@type': 'Question',
        name: '黑料在线观看是什么平台？',
        acceptedAnswer: {
          '@type': 'Answer',
          text: '黑料在线是一个汇聚最新最全视频资源的在线观看平台，支持免费在线播放，无需下载，高清流畅观看体验。我们每日更新海量视频内容，覆盖明星八卦、网红爆料、社会热点、影视娱乐等多个领域。',
        },
      },
      {
        '@type': 'Question',
        name: '黑料在线的视频需要付费吗？',
        acceptedAnswer: {
          '@type': 'Answer',
          text: '不需要。黑料在线所有视频内容均免费提供在线观看，无需会员注册，无需付费，打开网页即可直接观看。',
        },
      },
      {
        '@type': 'Question',
        name: '如何联系黑料在线客服？',
        acceptedAnswer: {
          '@type': 'Answer',
          text: '您可以通过拨打客服电话400-888-0000，或发送邮件至contact@heiliao-online.com联系我们。您也可以使用本页面的在线留言表单，我们的客服团队会在24小时内回复。',
        },
      },
    ],
  };

  return (
    <>
      <SEOMeta
        title="关于我们 - 黑料在线观看 | 品牌介绍与联系方式"
        description="黑料在线观看平台介绍 - 汇聚最新最全视频资源，免费在线播放。了解我们的品牌故事、用户口碑与联系方式。每日更新海量内容，高清流畅观看体验。"
        keywords="黑料在线观看,关于我们,品牌介绍,联系方式,视频平台"
        canonicalUrl="https://heiliao-online.com/about"
        schemaData={[orgSchema, faqSchema]}
      />

      <div className="min-h-screen bg-background-50">
        <Navbar />

        {/* Page Header */}
        <section className="relative w-full h-[200px] md:h-[260px] overflow-hidden pt-16">
          <img
            src="https://readdy.ai/api/search-image?query=Modern%20professional%20office%20environment%20with%20warm%20ambient%20lighting%2C%20team%20collaboration%20space%2C%20media%20company%20headquarters%2C%20clean%20minimalist%20design%2C%20editorial%20interior%20photography&width=1600&height=400&seq=about-header-2026&orientation=landscape"
            alt="关于黑料在线"
            className="absolute inset-0 w-full h-full object-cover object-top"
          />
          <div className="absolute inset-0 bg-gradient-to-b from-black/40 via-black/25 to-black/50"></div>
          <div className="absolute inset-0 flex items-center justify-center px-4">
            <div className="text-center">
              <h1 className="text-2xl md:text-4xl font-bold font-heading text-background-50 mb-2">
                关于我们
              </h1>
              <p className="text-xs md:text-sm text-background-200">了解我们的品牌故事与使命</p>
            </div>
          </div>
        </section>

        <div className="w-full px-4 md:px-6">
          <div className="max-w-6xl mx-auto">
            <Breadcrumb
              items={[
                { label: '首页', href: '/' },
                { label: '关于我们' },
              ]}
            />
          </div>
        </div>

        {/* Brand Introduction */}
        <section className="w-full px-4 md:px-6 py-10 md:py-14">
          <div className="max-w-6xl mx-auto">
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-10 items-center">
              <div>
                <h2 className="text-xl md:text-2xl font-bold font-heading text-foreground-950 mb-5">
                  品牌故事
                </h2>
                <div className="space-y-4 text-sm md:text-base text-foreground-700 leading-relaxed">
                  <p>
                    <strong>黑料在线</strong>成立于2020年，是国内领先的视频内容聚合平台。我们致力于为用户提供最新、最全、最高清的视频观看体验。
                  </p>
                  <p>
                    平台汇聚了来自各个领域的优质视频内容，涵盖明星八卦、网红爆料、社会热点、影视娱乐、综艺节目、科技数码、财经商业等多个热门分类。无论您关注哪个领域，都能在黑料在线找到感兴趣的内容。
                  </p>
                  <p>
                    我们的核心理念是<strong>"让每个人都能轻松获取最新资讯"</strong>。所有视频内容均免费提供在线观看，无需下载任何软件，无需注册会员，打开网页即可享受高清流畅的观看体验。
                  </p>
                  <p>
                    经过多年的发展，黑料在线已经积累了数千万的活跃用户，日均视频播放量超过5000万次，在行业中建立起良好的口碑和品牌影响力。
                  </p>
                </div>
              </div>
              <div className="rounded-xl overflow-hidden h-[300px] md:h-[380px]">
                <img
                  src="https://readdy.ai/api/search-image?query=Modern%20creative%20office%20space%20with%20collaborative%20work%20environment%2C%20digital%20screens%20displaying%20content%2C%20warm%20natural%20lighting%2C%20professional%20media%20company%20atmosphere%2C%20editorial%20photography&width=800&height=600&seq=about-brand-2026&orientation=landscape"
                  alt="黑料在线品牌故事"
                  className="w-full h-full object-cover"
                />
              </div>
            </div>
          </div>
        </section>

        {/* Stats */}
        <section className="w-full px-4 md:px-6 py-10 md:py-14 bg-background-100">
          <div className="max-w-6xl mx-auto">
            <div className="grid grid-cols-2 md:grid-cols-4 gap-6">
              <div className="text-center p-6 bg-background-50 rounded-xl">
                <div className="text-3xl md:text-4xl font-bold font-heading text-accent-500 mb-2">5000万+</div>
                <div className="text-sm text-foreground-600">日均播放量</div>
              </div>
              <div className="text-center p-6 bg-background-50 rounded-xl">
                <div className="text-3xl md:text-4xl font-bold font-heading text-primary-500 mb-2">3000万+</div>
                <div className="text-sm text-foreground-600">活跃用户</div>
              </div>
              <div className="text-center p-6 bg-background-50 rounded-xl">
                <div className="text-3xl md:text-4xl font-bold font-heading text-accent-500 mb-2">10万+</div>
                <div className="text-sm text-foreground-600">视频资源</div>
              </div>
              <div className="text-center p-6 bg-background-50 rounded-xl">
                <div className="text-3xl md:text-4xl font-bold font-heading text-primary-500 mb-2">8大类</div>
                <div className="text-sm text-foreground-600">内容分类</div>
              </div>
            </div>
          </div>
        </section>

        {/* User Testimonials */}
        <section className="w-full px-4 md:px-6 py-10 md:py-14">
          <div className="max-w-6xl mx-auto">
            <h2 className="text-xl md:text-2xl font-bold font-heading text-foreground-950 mb-8 text-center">
              用户口碑
            </h2>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
              <div className="p-6 bg-background-100 rounded-xl">
                <div className="flex items-center gap-1 mb-3">
                  {[...Array(5)].map((_, i) => (
                    <i key={i} className="ri-star-fill text-accent-500 text-sm"></i>
                  ))}
                </div>
                <p className="text-sm text-foreground-700 leading-relaxed mb-4">
                  "每天都会刷黑料在线，内容更新很快，视频清晰度也很高。八卦新闻总能第一时间在这里看到，已经推荐给身边很多朋友了。"
                </p>
                <div className="flex items-center gap-3">
                  <div className="w-9 h-9 rounded-full bg-secondary-200 flex items-center justify-center">
                    <i className="ri-user-line text-secondary-600"></i>
                  </div>
                  <div>
                    <div className="text-sm font-medium text-foreground-900">小王</div>
                    <div className="text-xs text-foreground-500">忠实用户 · 3年</div>
                  </div>
                </div>
              </div>
              <div className="p-6 bg-background-100 rounded-xl">
                <div className="flex items-center gap-1 mb-3">
                  {[...Array(5)].map((_, i) => (
                    <i key={i} className="ri-star-fill text-accent-500 text-sm"></i>
                  ))}
                </div>
                <p className="text-sm text-foreground-700 leading-relaxed mb-4">
                  "分类做得特别细致，想看什么直接点分类就能找到。不用下载APP这一点太方便了，随时随地打开浏览器就能看。"
                </p>
                <div className="flex items-center gap-3">
                  <div className="w-9 h-9 rounded-full bg-primary-200 flex items-center justify-center">
                    <i className="ri-user-line text-primary-600"></i>
                  </div>
                  <div>
                    <div className="text-sm font-medium text-foreground-900">小李</div>
                    <div className="text-xs text-foreground-500">活跃用户 · 2年</div>
                  </div>
                </div>
              </div>
              <div className="p-6 bg-background-100 rounded-xl">
                <div className="flex items-center gap-1 mb-3">
                  {[...Array(5)].map((_, i) => (
                    <i key={i} className="ri-star-fill text-accent-500 text-sm"></i>
                  ))}
                </div>
                <p className="text-sm text-foreground-700 leading-relaxed mb-4">
                  "视频加载速度很快，画质也很高清。作为一个视频内容爱好者，黑料在线完全满足了我的需求，真心推荐！"
                </p>
                <div className="flex items-center gap-3">
                  <div className="w-9 h-9 rounded-full bg-accent-200 flex items-center justify-center">
                    <i className="ri-user-line text-accent-600"></i>
                  </div>
                  <div>
                    <div className="text-sm font-medium text-foreground-900">小张</div>
                    <div className="text-xs text-foreground-500">新用户 · 6个月</div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* Contact Form */}
        <section className="w-full px-4 md:px-6 py-10 md:py-14 bg-background-100">
          <div className="max-w-3xl mx-auto">
            <h2 className="text-xl md:text-2xl font-bold font-heading text-foreground-950 mb-8 text-center">
              联系我们
            </h2>

            <div className="bg-background-50 rounded-xl p-6 md:p-8">
              {formStatus === 'success' ? (
                <div className="text-center py-8">
                  <div className="w-16 h-16 mx-auto mb-4 flex items-center justify-center rounded-full bg-accent-100">
                    <i className="ri-check-line text-2xl text-accent-600"></i>
                  </div>
                  <h3 className="text-lg font-bold text-foreground-900 mb-2">提交成功！</h3>
                  <p className="text-sm text-foreground-600">感谢您的留言，我们会在24小时内回复您。</p>
                  <button
                    onClick={() => setFormStatus('idle')}
                    className="mt-6 px-6 py-2 bg-primary-500 text-background-50 rounded-full text-sm font-medium hover:bg-primary-600 transition-colors whitespace-nowrap"
                  >
                    继续留言
                  </button>
                </div>
              ) : (
                <form data-readdy-form onSubmit={handleSubmit} className="space-y-5">
                  {formStatus === 'error' && formError && (
                    <div className="p-3 bg-primary-50 text-primary-700 rounded-lg text-sm">
                      <i className="ri-error-warning-line mr-1.5"></i>
                      {formError}
                    </div>
                  )}

                  {/* Honeypot */}
                  <input
                    type="text"
                    name="phone_alt"
                    tabIndex={-1}
                    autoComplete="off"
                    aria-hidden="true"
                    readOnly
                    className="honeypot-field"
                  />

                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                    <div>
                      <label htmlFor="contact_name" className="block text-sm font-medium text-foreground-700 mb-1.5">
                        姓名 <span className="text-primary-500">*</span>
                      </label>
                      <input
                        type="text"
                        id="contact_name"
                        name="name"
                        required
                        className="w-full px-4 py-2.5 rounded-lg border border-background-300 bg-background-50 text-sm text-foreground-900 placeholder-foreground-400 focus:outline-none focus:ring-2 focus:ring-primary-300 focus:border-primary-300 transition-colors"
                        placeholder="请输入您的姓名"
                      />
                    </div>
                    <div>
                      <label htmlFor="contact_email" className="block text-sm font-medium text-foreground-700 mb-1.5">
                        邮箱 <span className="text-primary-500">*</span>
                      </label>
                      <input
                        type="email"
                        id="contact_email"
                        name="email"
                        required
                        className="w-full px-4 py-2.5 rounded-lg border border-background-300 bg-background-50 text-sm text-foreground-900 placeholder-foreground-400 focus:outline-none focus:ring-2 focus:ring-primary-300 focus:border-primary-300 transition-colors"
                        placeholder="请输入您的邮箱"
                      />
                    </div>
                  </div>

                  <div>
                    <label htmlFor="contact_subject" className="block text-sm font-medium text-foreground-700 mb-1.5">
                      主题
                    </label>
                    <input
                      type="text"
                      id="contact_subject"
                      name="subject"
                      className="w-full px-4 py-2.5 rounded-lg border border-background-300 bg-background-50 text-sm text-foreground-900 placeholder-foreground-400 focus:outline-none focus:ring-2 focus:ring-primary-300 focus:border-primary-300 transition-colors"
                      placeholder="请输入留言主题"
                    />
                  </div>

                  <div>
                    <label htmlFor="contact_message" className="block text-sm font-medium text-foreground-700 mb-1.5">
                      留言内容 <span className="text-primary-500">*</span>
                    </label>
                    <textarea
                      id="contact_message"
                      name="message"
                      required
                      maxLength={500}
                      rows={5}
                      className="w-full px-4 py-2.5 rounded-lg border border-background-300 bg-background-50 text-sm text-foreground-900 placeholder-foreground-400 focus:outline-none focus:ring-2 focus:ring-primary-300 focus:border-primary-300 transition-colors resize-none"
                      placeholder="请输入您的留言内容（限500字以内）"
                    ></textarea>
                    <p className="text-xs text-foreground-400 mt-1">限500字以内</p>
                  </div>

                  <button
                    type="submit"
                    disabled={formStatus === 'submitting'}
                    className="w-full py-3 bg-primary-500 text-background-50 rounded-lg font-medium text-sm hover:bg-primary-600 transition-colors disabled:opacity-60 disabled:cursor-not-allowed whitespace-nowrap"
                  >
                    {formStatus === 'submitting' ? (
                      <span className="flex items-center justify-center gap-2">
                        <i className="ri-loader-4-line animate-spin"></i>提交中...
                      </span>
                    ) : (
                      '提交留言'
                    )}
                  </button>
                </form>
              )}
            </div>
          </div>
        </section>

        {/* Contact Info */}
        <section className="w-full px-4 md:px-6 py-10 md:py-14">
          <div className="max-w-6xl mx-auto">
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
              <div className="flex items-start gap-4 p-5 bg-background-100 rounded-xl">
                <div className="w-11 h-11 flex items-center justify-center rounded-full bg-primary-100 flex-shrink-0">
                  <i className="ri-phone-line text-primary-600 text-lg"></i>
                </div>
                <div>
                  <h4 className="text-base font-semibold text-foreground-900 mb-1">电话咨询</h4>
                  <p className="text-sm text-foreground-600">400-888-0000</p>
                  <p className="text-xs text-foreground-400 mt-1">工作日 9:00-18:00</p>
                </div>
              </div>
              <div className="flex items-start gap-4 p-5 bg-background-100 rounded-xl">
                <div className="w-11 h-11 flex items-center justify-center rounded-full bg-accent-100 flex-shrink-0">
                  <i className="ri-mail-line text-accent-600 text-lg"></i>
                </div>
                <div>
                  <h4 className="text-base font-semibold text-foreground-900 mb-1">邮箱联系</h4>
                  <p className="text-sm text-foreground-600">contact@heiliao-online.com</p>
                  <p className="text-xs text-foreground-400 mt-1">24小时内回复</p>
                </div>
              </div>
              <div className="flex items-start gap-4 p-5 bg-background-100 rounded-xl">
                <div className="w-11 h-11 flex items-center justify-center rounded-full bg-secondary-100 flex-shrink-0">
                  <i className="ri-map-pin-line text-secondary-600 text-lg"></i>
                </div>
                <div>
                  <h4 className="text-base font-semibold text-foreground-900 mb-1">公司地址</h4>
                  <p className="text-sm text-foreground-600">北京市朝阳区媒体大道88号</p>
                  <p className="text-xs text-foreground-400 mt-1">国贸中心A座15层</p>
                </div>
              </div>
            </div>
          </div>
        </section>

        <Footer />
      </div>
    </>
  );
}